import { useEffect, useState } from "react";

import * as api from "../lib/api";
import { SessionExpired } from "../lib/api";
import type { AppConfig } from "../lib/config";
import type { Identity } from "../lib/auth";
import type { SortKey, Student } from "../lib/types";

interface Props {
  config: AppConfig;
  identity: Identity;
  program: string;
  sort: SortKey;
  refreshKey: number;
  selectedId: string | null;
  onSelect: (studentId: string) => void;
  onSessionExpired: () => void;
}

export default function StudentList({
  config,
  identity,
  program,
  sort,
  refreshKey,
  selectedId,
  onSelect,
  onSessionExpired,
}: Props) {
  const [students, setStudents] = useState<Student[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listStudents(config, identity, program, sort)
      .then((result) => {
        if (cancelled) return;
        setStudents(result.students);
        setCursor(result.next_cursor);
        setTotal(result.total ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpired) onSessionExpired();
        else setError(err instanceof Error ? err.message : "Could not load students.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [config, identity, program, sort, refreshKey, onSessionExpired]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const result = await api.listStudents(config, identity, program, sort, cursor);
      setStudents((prev) => [...prev, ...result.students]);
      setCursor(result.next_cursor);
    } catch (err) {
      if (err instanceof SessionExpired) onSessionExpired();
      else setError(err instanceof Error ? err.message : "Could not load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  if (loading) return <p className="p-4 text-sm text-slate-500">Loading students…</p>;
  if (error)
    return (
      <p className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {error}
      </p>
    );

  return (
    // The rows scroll; "Load more" stays pinned to the bottom of the panel, where it
    // is still reachable without scrolling to the end of what is already loaded.
    <div className="flex min-h-0 flex-1 flex-col">
      {/* How many there are in total, so reaching the last loaded row does not read
          as "that is the whole program". */}
      <p className="shrink-0 border-b border-slate-100 px-3 py-2 text-xs text-slate-400">
        {total === null
          ? `${students.length} students`
          : `${students.length} of ${total.toLocaleString()} students`}
      </p>
      <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
        {students.map((s) => (
          <li key={s.student_id}>
            <button
              onClick={() => onSelect(s.student_id)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 ${
                selectedId === s.student_id ? "bg-slate-100" : ""
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {s.preferred_name} {s.family_name}
                </span>
                <span className="block text-xs text-slate-400">
                  {s.student_id} · WAM {s.wam ?? "—"} · {s.credit_points_completed ?? "—"} cp
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {cursor && (
        <div className="shrink-0 border-t border-slate-100 p-3">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="w-full rounded-md border border-slate-300 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
